import { getCurrentUser } from '../data/auth.js';

export const ADMIN_EMAILS = [
    'atworkwo@gmail.com',
    'santiagomerlot@proton.me',
];

export class FundAdmin {
    constructor(fund) {
        this.fund = fund;
    }

    static getAdminEmail() {
        const user = getCurrentUser();
        if (user?.email) return user.email.toLowerCase();
        const stored = localStorage.getItem('fg_user_email');
        return stored ? stored.toLowerCase() : '';
    }

    isAdmin() {
        const email = FundAdmin.getAdminEmail();
        return ADMIN_EMAILS.some((e) => e.toLowerCase() === email);
    }

    getProjectStatus(project) {
        const now = Date.now();
        const end = new Date(project.endDate).getTime();
        const pct = Math.min(Math.round((project.raised / project.target) * 100), 100);
        const daysLeft = Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));

        if (end < now) return { key: 'ended', label: 'Ended', className: 'fg-fund-admin__badge--ended' };
        if (pct >= 100) return { key: 'funded', label: 'Funded', className: 'fg-fund-admin__badge--funded' };
        if (daysLeft <= 5) return { key: 'ending', label: 'Ending Soon', className: 'fg-fund-admin__badge--ending' };
        return { key: 'active', label: 'Active', className: 'fg-fund-admin__badge--active' };
    }

    computeStats() {
        const now = Date.now();
        const monthStart = new Date();
        monthStart.setDate(1);
        monthStart.setHours(0, 0, 0, 0);
        const monthStartMs = monthStart.getTime();

        let totalRaised = 0;
        let totalDonors = 0;
        let active = 0;
        let completed = 0;
        let newCampaignsMonth = 0;
        let newDonorsMonth = 0;

        this.fund.projects.forEach((p) => {
            totalRaised += p.raised || 0;
            totalDonors += p.donorsCount || 0;
            const end = new Date(p.endDate).getTime();
            if (end >= now) active += 1;
            else completed += 1;

            if (p.createdAt && p.createdAt >= monthStartMs) newCampaignsMonth += 1;

            (p.comments || []).forEach((c) => {
                if (c.timestamp) {
                    const d = new Date(c.timestamp);
                    if (!Number.isNaN(d.getTime()) && d.getTime() >= monthStartMs) {
                        newDonorsMonth += 1;
                    }
                }
            });
        });

        const platformRevenue = Math.round(totalRaised * 0.05);

        return {
            totalRaised,
            platformRevenue,
            active,
            completed,
            totalDonors,
            newCampaignsMonth,
            newDonorsMonth,
        };
    }

    getRecentDonations(limit = 20) {
        const rows = [];
        this.fund.projects.forEach((p) => {
            (p.comments || []).forEach((c) => {
                rows.push({
                    donorName: c.donorName || 'Anonymous',
                    amount: c.amount || 0,
                    projectTitle: p.title,
                    projectId: p.id,
                    message: c.message || '',
                    timestamp: c.timestamp || '',
                });
            });
        });

        return rows
            .sort((a, b) => {
                const ta = new Date(a.timestamp).getTime();
                const tb = new Date(b.timestamp).getTime();
                return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
            })
            .slice(0, limit);
    }

    estimateLocalStorageUsage() {
        let bytes = 0;
        try {
            for (let i = 0; i < localStorage.length; i += 1) {
                const key = localStorage.key(i);
                const val = localStorage.getItem(key) || '';
                bytes += (key.length + val.length) * 2;
            }
        } catch {
            bytes = 0;
        }
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }

    render() {
        const stats = this.computeStats();
        const meta = this.fund.getFundMeta();
        const featuredSet = new Set(meta.featuredIds || []);
        const donations = this.getRecentDonations(20);

        const statsHtml = `
            <div class="fg-fund-admin__stats">
                <div class="fg-fund-admin__stat-card">
                    <span class="fg-fund-admin__stat-value">€${stats.totalRaised.toLocaleString()}</span>
                    <span class="fg-fund-admin__stat-label">Total raised</span>
                </div>
                <div class="fg-fund-admin__stat-card">
                    <span class="fg-fund-admin__stat-value">€${stats.platformRevenue.toLocaleString()}</span>
                    <span class="fg-fund-admin__stat-label">Platform revenue (5%)</span>
                </div>
                <div class="fg-fund-admin__stat-card">
                    <span class="fg-fund-admin__stat-value">${stats.active}</span>
                    <span class="fg-fund-admin__stat-label">Active campaigns</span>
                </div>
                <div class="fg-fund-admin__stat-card">
                    <span class="fg-fund-admin__stat-value">${stats.completed}</span>
                    <span class="fg-fund-admin__stat-label">Completed campaigns</span>
                </div>
                <div class="fg-fund-admin__stat-card">
                    <span class="fg-fund-admin__stat-value">${stats.totalDonors}</span>
                    <span class="fg-fund-admin__stat-label">Total donors</span>
                </div>
                <div class="fg-fund-admin__stat-card">
                    <span class="fg-fund-admin__stat-value">${stats.newCampaignsMonth}</span>
                    <span class="fg-fund-admin__stat-label">New campaigns this month</span>
                </div>
                <div class="fg-fund-admin__stat-card">
                    <span class="fg-fund-admin__stat-value">${stats.newDonorsMonth}</span>
                    <span class="fg-fund-admin__stat-label">New donors this month</span>
                </div>
            </div>
        `;

        const tableRows = this.fund.projects.map((p) => {
            const pct = Math.min(Math.round((p.raised / p.target) * 100), 100);
            const daysLeft = Math.max(0, Math.ceil((new Date(p.endDate) - new Date()) / (1000 * 60 * 60 * 24)));
            const status = this.getProjectStatus(p);
            const isFeatured = featuredSet.has(p.id);

            return `
                <tr data-project-id="${p.id}">
                    <td>${p.title}</td>
                    <td>${p.category}</td>
                    <td>€${p.target.toLocaleString()}</td>
                    <td>€${p.raised.toLocaleString()}</td>
                    <td>${pct}%</td>
                    <td>${p.donorsCount || 0}</td>
                    <td>${daysLeft}</td>
                    <td><span class="fg-fund-admin__badge ${status.className}">${status.label}</span></td>
                    <td class="fg-fund-admin__actions">
                        <button type="button" class="fg-fund-admin__btn" data-admin-action="view" data-id="${p.id}">View</button>
                        <button type="button" class="fg-fund-admin__btn" data-admin-action="feature" data-id="${p.id}">
                            ${isFeatured ? 'Unfeature' : 'Feature'}
                        </button>
                        <button type="button" class="fg-fund-admin__btn fg-fund-admin__btn--danger" data-admin-action="remove" data-id="${p.id}">Remove</button>
                    </td>
                </tr>
            `;
        }).join('');

        const donationsHtml = donations.length > 0 ? donations.map((d) => `
            <div class="fg-fund-admin__donation-item">
                <div class="fg-fund-admin__donation-header">
                    <strong>${d.donorName}</strong>
                    <span>€${Number(d.amount).toLocaleString()}</span>
                </div>
                <p class="fg-fund-admin__donation-project">${d.projectTitle}</p>
                ${d.message ? `<p class="fg-fund-admin__donation-message">"${d.message}"</p>` : ''}
                <span class="fg-fund-admin__donation-date">${d.timestamp}</span>
            </div>
        `).join('') : '<p class="fg-fund-empty-sub">No donations recorded yet.</p>';

        return `
            <div class="fg-fund-admin animate-fade-in">
                <button type="button" class="fg-fund-back-link" id="admin-back-browse">← Back to Browse</button>
                <header class="fg-fund-admin__header">
                    <h2>Fund Admin Dashboard</h2>
                    <p>Manage campaigns, donations, and platform settings.</p>
                </header>

                ${statsHtml}

                <section class="fg-fund-admin__section">
                    <h3>Campaigns</h3>
                    <div class="fg-fund-admin__table-wrap">
                        <table class="fg-fund-admin__table">
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Category</th>
                                    <th>Target</th>
                                    <th>Raised</th>
                                    <th>% Funded</th>
                                    <th>Donors</th>
                                    <th>Days Left</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>${tableRows}</tbody>
                        </table>
                    </div>
                </section>

                <section class="fg-fund-admin__section">
                    <h3>Recent Donations</h3>
                    <div class="fg-fund-admin__donations-feed">${donationsHtml}</div>
                </section>

                <section class="fg-fund-admin__section fg-fund-admin__quick-actions">
                    <h3>Quick Actions</h3>
                    <div class="fg-fund-admin__actions-bar">
                        <button type="button" class="fg-fund-btn fg-fund-btn--primary" id="admin-export-json">Export campaigns (JSON)</button>
                        <button type="button" class="fg-fund-btn" id="admin-export-csv">Export donations (CSV)</button>
                        <button type="button" class="fg-fund-btn fg-fund-admin__btn--danger" id="admin-clear-test">Clear test data</button>
                    </div>
                    <div class="fg-field" style="margin-top:16px;">
                        <label for="admin-hero-banner">Featured hero banner text</label>
                        <input type="text" id="admin-hero-banner" class="fg-input" value="${meta.heroBanner || ''}" placeholder="Optional message shown in browse hero">
                        <button type="button" class="fg-fund-btn" id="admin-save-hero" style="margin-top:8px; width:auto; padding:8px 16px;">Save banner text</button>
                    </div>
                </section>

                <section class="fg-fund-admin__section fg-fund-admin__health">
                    <h3>Platform Health</h3>
                    <p>localStorage usage (estimate): <strong>${this.estimateLocalStorageUsage()}</strong></p>
                    <p class="fg-fund-admin__health-note">Connect backend database to scale beyond local storage.</p>
                    <a href="https://supabase.com/dashboard" target="_blank" rel="noopener noreferrer" class="fg-fund-admin__link">Set up Supabase (future task) →</a>
                </section>
            </div>
        `;
    }

    attachEvents(root) {
        root.querySelector('#admin-back-browse')?.addEventListener('click', () => {
            this.fund.currentView = 'browse';
            this.fund.render();
        });

        root.querySelectorAll('[data-admin-action]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const action = btn.dataset.adminAction;
                const id = btn.dataset.id;
                if (action === 'view') {
                    this.fund.selectedProjectId = id;
                    this.fund.currentView = 'detail';
                    this.fund.render();
                } else if (action === 'feature') {
                    this.fund.toggleFeaturedProject(id);
                    this.fund.currentView = 'admin';
                    this.fund.render();
                } else if (action === 'remove') {
                    if (confirm('Remove this campaign from local storage? This cannot be undone.')) {
                        this.fund.removeProject(id);
                        this.fund.currentView = 'admin';
                        this.fund.render();
                    }
                }
            });
        });

        root.querySelector('#admin-export-json')?.addEventListener('click', () => {
            this.downloadFile(
                JSON.stringify(this.fund.projects, null, 2),
                'fg-fund-campaigns.json',
                'application/json'
            );
        });

        root.querySelector('#admin-export-csv')?.addEventListener('click', () => {
            const rows = [['donor', 'amount', 'project', 'projectId', 'message', 'timestamp']];
            this.getRecentDonations(500).forEach((d) => {
                rows.push([
                    d.donorName,
                    String(d.amount),
                    d.projectTitle,
                    d.projectId,
                    d.message.replace(/"/g, '""'),
                    d.timestamp,
                ]);
            });
            const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
            this.downloadFile(csv, 'fg-fund-donations.csv', 'text/csv');
        });

        root.querySelector('#admin-clear-test')?.addEventListener('click', () => {
            if (confirm('Remove all sample campaigns and reset fund data? User-created campaigns will also be cleared.')) {
                localStorage.removeItem('fg_fund_projects');
                localStorage.removeItem('fg_fund_meta');
                this.fund.initData();
                this.fund.currentView = 'admin';
                this.fund.render();
            }
        });

        root.querySelector('#admin-save-hero')?.addEventListener('click', () => {
            const text = root.querySelector('#admin-hero-banner')?.value?.trim() || '';
            const meta = this.fund.getFundMeta();
            meta.heroBanner = text;
            this.fund.saveFundMeta(meta);
            alert('Hero banner text saved.');
        });
    }

    downloadFile(content, filename, mime) {
        const blob = new Blob([content], { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    }
}
